import React from "react";

const CONTROL_SCALE = 0.8;

function scaleControlValue(value) {
  return typeof value === "number" ? Math.round(value * CONTROL_SCALE) : value;
}

function scaleControlStyle(style = {}) {
  const next = { ...style };
  ["width", "height", "minWidth", "minHeight", "maxWidth", "maxHeight"].forEach((key) => {
    if (typeof next[key] === "number") next[key] = scaleControlValue(next[key]);
  });
  if (typeof next.paddingInline === "number") next.paddingInline = scaleControlValue(next.paddingInline);
  return next;
}

export function QuickIconButton({
  label,
  title,
  active = false,
  disabled = false,
  onClick,
  minWidth = 42,
  style = {},
  indicatorColor = "",
  cornerActionTitle = "",
  onCornerAction,
  cornerActionActive = false,
  cornerActionLabel = "⋯",
}) {
  const hasIndicator = typeof indicatorColor === "string" && indicatorColor.trim().length > 0;
  const hasCornerAction = typeof onCornerAction === "function";
  const scaledStyle = scaleControlStyle(style);
  const handleCornerAction = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    onCornerAction?.();
  };
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
      }}
    >
      <button
        className="btn btn-secondary"
        type="button"
        onClick={onClick}
        onContextMenu={(e) => e.preventDefault()}
        disabled={disabled}
        title={title}
        aria-label={title}
        style={{
          minWidth: scaleControlValue(minWidth),
          height: 34,
          padding: "0 8px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          fontSize: 15,
          lineHeight: 1,
          boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
          borderColor: active ? "#f59e0b" : undefined,
          color: active ? "#fbbf24" : undefined,
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
          ...scaledStyle,
        }}
      >
        <span aria-hidden="true">{label}</span>
        {hasIndicator && active ? (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 6,
              right: 6,
              bottom: 4,
              height: 4,
              borderRadius: 999,
              background: indicatorColor,
              border: "1px solid rgba(255,255,255,0.32)",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.15) inset",
              pointerEvents: "none",
            }}
          />
        ) : null}
      </button>
      {hasCornerAction ? (
        <button
          type="button"
          className="btn btn-secondary"
          disabled={disabled}
          title={cornerActionTitle || "Opcje"}
          aria-label={cornerActionTitle || "Opcje"}
          onMouseDown={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          onClick={handleCornerAction}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleCornerAction(e);
          }}
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            width: 11,
            height: 11,
            minWidth: 11,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 8,
            fontWeight: 800,
            lineHeight: 1,
            padding: 0,
            color: cornerActionActive ? "#fbbf24" : undefined,
            borderColor: cornerActionActive ? "#f59e0b" : undefined,
            boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
            cursor: disabled ? "default" : "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            WebkitTapHighlightColor: "transparent",
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {cornerActionLabel}
        </button>
      ) : null}
    </span>
  );
}

export function StylusModeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <g transform="rotate(-25 12 12)">
        <path d="M6.5 16.2 L10.8 20.5 L18.6 12.7 L14.3 8.4 Z" fill="currentColor" />
        <path d="M13.9 8 L16.9 5 C17.7 4.2 19 4.2 19.8 5 L20.6 5.8 C21.4 6.6 21.4 7.9 20.6 8.7 L17.6 11.7 Z" fill="#f59e0b" />
        <path d="M6.2 16.5 L10.5 20.8 L4.8 22.2 Z" fill="#fb7185" />
      </g>
      <circle cx="4.2" cy="5" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function ToolGroupIcon({ open = false }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 7.5H15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M5 12H15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M5 16.5H15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d={open ? "M17.5 14.5L20.5 11.5L23.5 14.5" : "M17.5 9.5L20.5 12.5L23.5 9.5"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TouchPanIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M10 4.5a1.2 1.2 0 0 1 2.4 0V11l.9-.9a1.2 1.2 0 0 1 1.7 0l.1.1.8-.8a1.2 1.2 0 0 1 1.7 0l.4.4a1.2 1.2 0 0 1 0 1.7l.8.8a1.2 1.2 0 0 1 0 1.7l-3.6 3.6a3.8 3.8 0 0 1-2.7 1.1H11a4 4 0 0 1-4-4v-3a1.2 1.2 0 0 1 2.4 0V13h.6V4.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 7h3M18 7h3M12 1v3M12 20v3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function PenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
      <g transform="rotate(-25 16 16)">
        <path d="M9 22 L13 26 L24 15 L20 11 Z" fill="currentColor" />
        <path d="M19.5 10.5 L22.8 7.2 C23.6 6.4 24.9 6.4 25.7 7.2 L26.8 8.3 C27.6 9.1 27.6 10.4 26.8 11.2 L23.5 14.5 Z" fill="#f59e0b" />
        <path d="M8.4 22.6 L12.2 26.4 L7 27.8 Z" fill="#fb7185" />
      </g>
    </svg>
  );
}

export function EraserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
      <g transform="rotate(-25 16 16)">
        <rect x="8" y="11" width="16" height="10" rx="2" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.5" />
        <rect x="8" y="11" width="6" height="10" rx="2" fill="#fb7185" />
      </g>
    </svg>
  );
}

export function HighlighterIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
      <g transform="rotate(-25 16 16)">
        <rect x="8" y="9" width="16" height="8" rx="2" fill="#fbbf24" />
        <path d="M9 17 H23 V22 C23 23.1 22.1 24 21 24 H11 C9.9 24 9 23.1 9 22 Z" fill="currentColor" />
        <rect x="11" y="21" width="10" height="3" rx="1.5" fill="#fde68a" opacity="0.7" />
      </g>
    </svg>
  );
}

export function LineIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 18 L19 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="5" cy="18" r="1.7" fill="currentColor" />
      <circle cx="19" cy="6" r="1.7" fill="currentColor" />
    </svg>
  );
}

export function PolygonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 5.1 L18.5 9.7 L16 17.9 L8 17.9 L5.5 9.7 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="5.1" r="1.3" fill="currentColor" />
      <circle cx="18.5" cy="9.7" r="1.3" fill="currentColor" />
      <circle cx="16" cy="17.9" r="1.3" fill="currentColor" />
      <circle cx="8" cy="17.9" r="1.3" fill="currentColor" />
      <circle cx="5.5" cy="9.7" r="1.3" fill="currentColor" />
    </svg>
  );
}

export function CircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="17.2" cy="9.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

export function SelectIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
      <path
        d="M7 5 L21 18 H14 L12 27 L8.8 25.8 L10.8 17.8 L5 17 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.2"
      />
    </svg>
  );
}

export function ImageIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="10" r="1.6" fill="currentColor" />
      <path
        d="M6.5 16.5 L10.5 12.5 L13.5 15.5 L16 13 L19 16.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TaskIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="3.5" width="16" height="17" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8.5H16M8 12H16M8 15.5H13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="15.8" cy="15.8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M17.3 17.3 L19.4 19.4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function LinkChainIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M10.2 13.8 L8.4 15.6 C7 17 4.8 17 3.4 15.6 C2 14.2 2 12 3.4 10.6 L7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.8 10.2 L15.6 8.4 C17 7 19.2 7 20.6 8.4 C22 9.8 22 12 20.6 13.4 L17 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8 16 L16 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function CopyCodeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="8" y="7" width="12" height="12" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M6 15 H4.8 A1.8 1.8 0 0 1 3 13.2 V4.8 A1.8 1.8 0 0 1 4.8 3 H13.2 A1.8 1.8 0 0 1 15 4.8 V6" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M10.8 11.2 L9 13 L10.8 14.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.2 11.2 L19 13 L17.2 14.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function TrashCanIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7 H20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 7 V5.5 A1.5 1.5 0 0 1 10.5 4 H13.5 A1.5 1.5 0 0 1 15 5.5 V7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <rect x="6.5" y="7" width="11" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10 10.5 V16.5 M14 10.5 V16.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function LeaveDoorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4.5 H13 A2 2 0 0 1 15 6.5 V17.5 A2 2 0 0 1 13 19.5 H4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 12 H21" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M18.5 9.5 L21 12 L18.5 14.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="10.2" cy="12" r="0.9" fill="currentColor" />
    </svg>
  );
}

export function GridIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9 4.5V20.5M15 4.5V20.5M4.5 9H20.5M4.5 15H20.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function UndoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M9 7 L5 11 L9 15"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 11 H14 C17.3 11 20 13.7 20 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function RedoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M15 7 L19 11 L15 15"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18 11 H10 C6.7 11 4 13.7 4 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FullscreenEnterIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 4H4V8M16 4H20V8M8 20H4V16M16 20H20V16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function FullscreenExitIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 9H4V4M15 9H20V4M9 15H4V20M15 15H20V20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 9L6 6M15 9L18 6M9 15L6 18M15 15L18 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export function ZoomControl({ zoomPercent, onZoomOut, onReset, onZoomIn, minZoom, maxZoom, themeMode = "dark" }) {
  const lightMode = themeMode === "light";
  const containerStyle = lightMode
    ? {
        border: "1px solid rgba(15,23,42,0.14)",
        background: "rgba(223, 241, 252, 0.95)",
        boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
      }
    : {
        border: "1px solid rgba(255,255,255,0.18)",
        background: "rgba(12,16,40,0.6)",
        boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
      };
  const buttonStyle = lightMode
    ? {
        background: "rgba(240, 250, 255, 0.95)",
        borderColor: "rgba(15,23,42,0.24)",
      }
    : {};

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: 3,
        borderRadius: 12,
        whiteSpace: "nowrap",
        flexShrink: 0,
        ...containerStyle,
      }}
    >
      <QuickIconButton label="-" title="Oddal" disabled={zoomPercent <= minZoom} onClick={onZoomOut} style={buttonStyle} />
      <QuickIconButton label={`${zoomPercent}%`} title={`Zoom ${zoomPercent}%`} onClick={onReset} minWidth={62} style={buttonStyle} />
      <QuickIconButton label="+" title="Przybliz" disabled={zoomPercent >= maxZoom} onClick={onZoomIn} style={buttonStyle} />
    </div>
  );
}
